import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatPrettyDate } from "@/lib/app-types";
import { buildCalendar, type StudyChapter } from "@/lib/lecture-plan";

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

function monthKey(year: number, month: number, day: number) {
  return `${year}-${`${month + 1}`.padStart(2, "0")}-${`${day}`.padStart(2, "0")}`;
}

export function StudyCalendar({
  chapters,
  today,
}: {
  chapters: StudyChapter[];
  today: string;
}) {
  const now = new Date(`${today}T00:00:00`);
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [selected, setSelected] = useState<string>(today);

  const calendar = buildCalendar(chapters, today);
  const first = new Date(cursor.year, cursor.month, 1);
  const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
  const offset = (first.getDay() + 6) % 7;
  const entry = calendar.get(selected);

  const shift = (delta: number) => {
    const d = new Date(cursor.year, cursor.month + delta, 1);
    setCursor({ year: d.getFullYear(), month: d.getMonth() });
  };

  return (
    <div className="glass rounded-[24px] p-5">
      <div className="flex items-center justify-between">
        <motion.button
          type="button"
          whileTap={{ scale: 0.9 }}
          onClick={() => shift(-1)}
          aria-label="Previous month"
          className="rounded-full bg-secondary/60 p-2 text-muted-foreground"
        >
          <ChevronLeft className="size-4" />
        </motion.button>
        <p className="text-sm font-bold tracking-tight">
          {first.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
        </p>
        <motion.button
          type="button"
          whileTap={{ scale: 0.9 }}
          onClick={() => shift(1)}
          aria-label="Next month"
          className="rounded-full bg-secondary/60 p-2 text-muted-foreground"
        >
          <ChevronRight className="size-4" />
        </motion.button>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground">
        {WEEKDAYS.map((d, i) => (
          <span key={`${d}-${i}`}>{d}</span>
        ))}
      </div>

      <div className="mt-1.5 grid grid-cols-7 gap-1">
        {Array.from({ length: offset }, (_, i) => (
          <span key={`pad-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const key = monthKey(cursor.year, cursor.month, day);
          const data = calendar.get(key);
          const missed = data?.revisions.some((r) => r.missed) ?? false;
          const isToday = key === today;
          const isSelected = key === selected;
          return (
            <motion.button
              key={key}
              type="button"
              whileTap={{ scale: 0.88 }}
              onClick={() => setSelected(key)}
              className={cn(
                "relative flex aspect-square flex-col items-center justify-center rounded-xl text-[12px] font-semibold transition-colors",
                isSelected
                  ? "text-primary-foreground [background-image:var(--gradient-brand)]"
                  : isToday
                    ? "border border-primary/40 text-primary"
                    : "text-muted-foreground",
              )}
            >
              {day}
              <span className="mt-0.5 flex h-1 gap-0.5">
                {data?.completions.length ? (
                  <span className="size-1 rounded-full bg-success" />
                ) : null}
                {data?.revisions.length ? (
                  <span className={cn("size-1 rounded-full", missed ? "bg-warning" : "bg-primary")} />
                ) : null}
              </span>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={selected}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          className="mt-4 border-t border-hairline pt-4"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {formatPrettyDate(selected)}
          </p>
          {entry && (entry.completions.length > 0 || entry.revisions.length > 0) ? (
            <div className="mt-2.5 space-y-2">
              {entry.completions.map((item, i) => (
                <div
                  key={`c-${i}`}
                  className="flex items-center gap-2 rounded-2xl border border-success/20 bg-success/8 px-3 py-2 text-[12px]"
                >
                  <span className="size-1.5 rounded-full bg-success" />
                  <span className="min-w-0 flex-1 truncate font-semibold">{item.chapter}</span>
                  <span className="text-muted-foreground">
                    {item.subject} · {item.lectures} done
                  </span>
                </div>
              ))}
              {entry.revisions.map((item, i) => (
                <div
                  key={`r-${i}`}
                  className={cn(
                    "flex items-center gap-2 rounded-2xl border px-3 py-2 text-[12px]",
                    item.missed ? "border-warning/25 bg-warning/8" : "border-primary/20 bg-primary/8",
                  )}
                >
                  <span className={cn("size-1.5 rounded-full", item.missed ? "bg-warning" : "bg-primary")} />
                  <span className="min-w-0 flex-1 truncate font-semibold">{item.chapter}</span>
                  <span className="text-muted-foreground">
                    {item.missed ? "Missed" : "Revision"} · {item.lectures}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-[12px] text-muted-foreground">Nothing scheduled on this day.</p>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}